import { yupResolver } from '@hookform/resolvers/yup';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoItem } from '@mui/x-date-pickers/internals/demo';
import LabelledTextField from 'components/Common/LabelledTextField';
import PhoneNumberField from 'components/Common/PhoneNumberField';
import { MyContext } from 'Context';
import translatedFormValidationSchemas from 'FormValidations/translatedFormValidationSchemas';
import { getLocalStorageItem } from 'hooks';
import { useAuthMe } from 'queries/AuthQueries';
import { usecreatePatientApiQuery, usemakeInDependent } from 'queries/Patient';
import React, { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { QueryClient } from 'react-query';
import HandleErrorResponse from 'utils/HandleErrorResponse';
import * as yup from 'yup';

import DobField from './DobField';

interface CreateUserForm {
  setOpen?: any;
  userdata: any;
}

const MakeIndependent: React.FC<CreateUserForm> = ({ setOpen, userdata }) => {
  const { data: profileData } = useAuthMe();
  const UserDetails = getLocalStorageItem('roles');
  const { t } = useTranslation('patients');
  const { t: translate } = useTranslation('validationErrorMessages');
  const { first_name, last_name, email, phone, dateofbirth } =
    translatedFormValidationSchemas(translate);

  const createUserSchema = yup.object().shape({
    first_name,
    last_name,
    email,
    phone,
    dateofbirth: yup.string(),
  });

  // console.log(userdata);
  // console.log();

  const { dependentsList, setDependentsList, dependentData } =
    useContext(MyContext);
  // console.log(dependentData?.phone);

  const queryClient = new QueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
  } = useForm<any>({
    defaultValues: {
      first_name: dependentData?.firstName,
      last_name: dependentData?.lastName,
      email: dependentData?.email,
    },
    resolver: yupResolver(createUserSchema),
  });

  const headerKeys = {
    'x-klynex-access-key':
      profileData?.data?.account.patientsAccessKey ||
      UserDetails?.account.patientsAccessKey,
  };

  const { mutate: createPatientApi } = usecreatePatientApiQuery(headerKeys);
  const { mutate: makeInDependent } = usemakeInDependent();
  const [errorMessage, setErrorMessage] = useState<null>(null);
  const [emailValue, setEmailValue] = useState<string>('email');
  const [dateValue, setDateValue] = useState<any>(
    dependentData?.patient?.dob
      ? new Date(dependentData.patient.dob)
      : new Date('2000-04-05')
  );
  const [phonenumber, setPhoneNumber] = useState<string | null>(
    dependentData?.phone
  );
  // const [dateValue, setDateValue] = useState<Date | null>(
  //   dependentData?.dob ? new Date(dependentData.dob) : new Date('2000-01-01')
  // );

  // console.log(dateValue);
  const handleChangeValue = (
    field: any,
    value: any,
    shouldValidate = false
  ) => {
    setValue(field, value, {
      shouldValidate: field in errors || shouldValidate,
    });
  };
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const handlePhoneNumber = ({ phone }: any) => {
    if (phone.includes('null')) {
      setPhoneNumber(null);
      handleChangeValue('phone', null);
    } else {
      setPhoneNumber(phone);
      handleChangeValue('phone', phone);
    }
  };
  const formatDate = (dateString = '') => {
    if (!dateString) return '';

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      console.error('Invalid date string:', dateString);
      return ''; // or handle the error appropriately
    }

    const dateFormatter = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    const formattedDate = dateFormatter.format(date);

    // Use split and join instead of replace
    const result = formattedDate.split(',')[0]?.split('/').join('.');

    return result;
  };

  const submitAuthForm = (data: any) => {
    const userData = getLocalStorageItem('roles');
    // const dobFormatted = dateValue ? format(dateValue, 'yyyy-MM-dd') : null;
    const userPayload: any = {
      firstname: data?.first_name,
      lastname: data?.last_name,
      // dob: dobFormatted,
      accountId: userData?.account?.id,
      // patientId: data?.employeeCode?.trim() || null,
      email: data?.email !== '' ? data?.email : null,
      phone: data?.phone !== '' ? data?.phone : null,
      uuid: userdata?.uuid,
      guardian_uuid: userData?.uuid,
      dob: formatDate(dateValue),
      headerKeys,
    };

    makeInDependent(userPayload, {
      onSuccess: () => {
        // setOpen(true);
        setErrorMessage(null);
        window.location.reload();
      },
      onError: (err: any) => {
        const message = err?.fieldErrors[0]?.message;
        // setOpen(true);
        setErrorMessage(message);
        HandleErrorResponse(err, setError);
      },
    });
    // queryClient.refetchQueries([queryKeys.AUTH_ME, Endpoints.AUTH_ME]);
  };
  const handleAuthErrors = (err: any) => {
    console.log(err, 'errors');
  };
  const handleEmail = (e: any) => {
    setEmailValue(e.target.value);
    handleChangeValue('email', e.target.value);
  };

  return (
    <>
      <form
        id="create-user"
        onSubmit={handleSubmit(submitAuthForm, handleAuthErrors)}
      >
        <DialogTitle>
          <Typography fontWeight={600} variant="h5" color="secondary.100">
            {t('makeindependent')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <div className="flex flex-col">
            <LabelledTextField
              className="mt-2"
              variant="outlined"
              label={t('lastName')}
              placeholder={`${t('enterLastName')}`}
              {...register('last_name')}
              error={Boolean(errors.last_name)}
              helperText={
                errors && errors.last_name && errors.last_name.message
              }
            />
            <LabelledTextField
              className="mt-2"
              variant="outlined"
              label={`${t('firstName')}`}
              placeholder={`${t('enterFirstName')}`}
              {...register('first_name')}
              error={Boolean(errors.first_name)}
              helperText={
                errors && errors.first_name && errors.first_name.message
              }
            />

            {/* <LabelledTextField
              className="mt-2"
              variant="outlined"
              label={`${t('patientId')}`}
              placeholder={`${t('enterPatientId')}`}
              {...register('employeeCode')}
              error={Boolean(errors.employeeCode)}
              helperText={
                errors && errors.employeeCode && errors.employeeCode.message
              }
            /> */}
            <Typography className="mt-1 text-red-500" fontSize={12}>
              {errorMessage}
            </Typography>
            <div className="mt-1">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoItem label={`${t('dateOfBirth')}`}>
                  <DobField
                    // className="w-[430px]"
                    value={dateValue}
                    onChange={(newValue: any) => {
                      setDateValue(newValue);
                      setValue('dateofbirth', newValue, {
                        shouldValidate: true,
                      });
                    }}
                    error={Boolean(errors.dateofbirth)}
                    helperText={errors.dateofbirth?.message}
                  />
                </DemoItem>
              </LocalizationProvider>
              <Typography className="mt-1 text-xs text-red-500">
                {errors?.dateofbirth?.message as string}
              </Typography>
            </div>
            <LabelledTextField
              className="mt-2"
              variant="outlined"
              label={`${t('email')}`}
              placeholder={`${t('enterEmail')}`}
              {...register('email')}
              onChange={(e: any) => {
                handleEmail(e);
              }}
              error={Boolean(errors.email)}
              helperText={errors && errors.email && errors.email.message}
            />
            <div className="mt-4">
              <PhoneNumberField
                labelText={`${t('phoneNo')}`}
                placeholder="555-000-0000"
                onChange={handlePhoneNumber}
                value={phonenumber}
                error={Boolean(errors.phone)}
                helperText={errors && errors.phone && errors.phone.message}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button form="create-user" type="submit" className="mr-4">
            {t('invite')}
          </Button>
        </DialogActions>
      </form>
    </>
  );
};

export default MakeIndependent;
